import { atom, useAtom } from "jotai";
import { environment } from "../environment";
import { loggedInUserAtom } from "../services/login/types";


export const socketAtom = atom<WebSocket | null>(null);
export const socketWeightMessage = atom<{[key: string]: any} | null>(null);
export const socketScannerMessage = atom<{[key: string]: any} | null>(null);
export const socketCancellationMessage = atom<{[key: string]: any} | null>(null);
export const socketDesignCreatedMessage = atom<{[key: string]: any} | null>(null);
export const socketPricingUpdateMessage = atom<{[key: string]: any} | null>(null);
// pull_flag_insert | pull_flag_acknowledged | pull_flag_removed — only delivered
// to sockets subscribed with all_brands (see subscribeAllBrands).
export const socketPullFlagMessage = atom<{[key: string]: any} | null>(null);

const PULL_FLAG_MESSAGE_TYPES = ['pull_flag_insert', 'pull_flag_acknowledged', 'pull_flag_removed'];

// Number of mounted consumers that want every brand's traffic on the shared
// socket. Module-level so it survives reconnects: a fresh socket's onopen
// subscribes all_brands instead of the user's brand while this is > 0.
let allBrandsSubscribers = 0;

type SocketFunctions = {
    sendMessage: (data: string) => void;
    connect: () => void;
    /**
     * Opt the shared socket in to every brand's traffic (internal surfaces only).
     * Returns a cleanup that drops back to the user's brand subscription once no
     * consumer needs all_brands any more. Call from a useEffect keyed on the socket.
     */
    subscribeAllBrands: () => () => void;
}

type UseSocket = [
    WebSocket | null,
    SocketFunctions,
    {[key: string]: any} | null,
    {[key: string]: any} | null,
    {[key: string]: any} | null,
    {[key: string]: any} | null,
];

export const useSocket = (): UseSocket => {
    const [socket, setSocket] = useAtom(socketAtom);
    const [weightMessage, setSocketWeightMessage] = useAtom(socketWeightMessage);
    const [scannerMessage, setSocketScannerMessage] = useAtom(socketScannerMessage);
    const [cancellationMessage, setSocketCancellationMessage] = useAtom(socketCancellationMessage);
    const [designCreatedMessage, setSocketDesignCreatedMessage] = useAtom(socketDesignCreatedMessage);
    const [, setSocketPricingUpdateMessage] = useAtom(socketPricingUpdateMessage);
    const [, setSocketPullFlagMessage] = useAtom(socketPullFlagMessage);
    const [user] = useAtom(loggedInUserAtom);

    const connect = async () => {
        const ws = new WebSocket(`${environment.socketUrl}`);
        ws.onopen = () => {
            // Declare which brand this socket is watching. The server routes
            // brand-scoped events (pricing_update_progress) only to subscribed
            // sockets — without this handshake we receive none of them.
            if (allBrandsSubscribers > 0) {
                ws.send(JSON.stringify({ type: 'subscribe', all_brands: true }));
            } else if (user?.brand_id) {
                ws.send(JSON.stringify({ type: 'subscribe', brand_id: user.brand_id }));
            }
        }
        ws.onclose = () => {
            setSocket(null);
        }
        ws.onmessage = (event: MessageEvent<any>) => {
            const data = JSON.parse(event.data);
            if (data.type === "weight") {
                setSocketWeightMessage(data);
            } else if (data.type === "scanner") {
                setSocketScannerMessage(data);
            } else if (data.type === "cancellation_insert") {
                setSocketCancellationMessage(data);
            } else if (PULL_FLAG_MESSAGE_TYPES.includes(data.type)) {
                setSocketPullFlagMessage(data);
            } else if (data.type === "design_created") {
                setSocketDesignCreatedMessage(data);
            } else if (data.type === "pricing_update_progress") {
                // Defence in depth: the server already scopes delivery by brand,
                // but never render another brand's job if one slips through.
                if (!user?.brand_id || data?.payload?.brand_id === user.brand_id) {
                    setSocketPricingUpdateMessage(data);
                }
            }
        }
        setSocket(ws);
    }

    const sendMessage = async (data: string) => {
        const ws = new WebSocket(`${environment.socketUrl}`);
        ws.onopen = () => {
            ws.send(data);
            ws.close();
        }
    }

    // #region subscribeAllBrands
    const subscribeAllBrands = () => {
        allBrandsSubscribers++;
        const ws = socket;
        // CONNECTING sockets pick this up in onopen via the counter.
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'subscribe', all_brands: true }));
        }
        let released = false;
        return () => {
            if (released) return;
            released = true;
            allBrandsSubscribers = Math.max(0, allBrandsSubscribers - 1);
            if (allBrandsSubscribers === 0 && ws && ws.readyState === WebSocket.OPEN && user?.brand_id) {
                ws.send(JSON.stringify({ type: 'subscribe', brand_id: user.brand_id }));
            }
        };
    }

    const socketFunctions: SocketFunctions = {
        sendMessage,
        connect,
        subscribeAllBrands
    }

    return [
        socket,
        socketFunctions,
        weightMessage,
        scannerMessage,
        cancellationMessage,
        designCreatedMessage,
    ]
}